import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 1. useTranslation 훅 추가
import DOMPurify from 'dompurify';
import CommentSection from '../../Comment/CommentSection';
import styles from './style/PostDetail.module.css';
import { useAuth } from 'react-oidc-context';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoginPromptToast from '../../partials/LoginPromptToast/LoginPromptToast.js';
import CommonStyles from './../../../styles/Common.module.css';
import 'react-quill/dist/quill.snow.css';
const API_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_PROFILE_URL = process.env.REACT_APP_DEFAULT_PROFILE_IMAGE;

export default function PostDetail() {

  
  const { t, i18n } = useTranslation(); // 2. 훅을 호출하고 t 함수와 i18n 객체를 가져옵니다.
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [postAuthorNickname, setPostAuthorNickname] = useState('');
  const [postAuthorProfileImageUrl, setPostAuthorProfileImageUrl] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);

  const auth = useAuth();
  const idToken = auth.user?.id_token;
  const currentUserId = auth.user?.profile?.sub;

  const showLoginToast = () => {
    toast.error(<LoginPromptToast signInRedirect={auth.signinRedirect} navigate={navigate} />, {
      position: "top-center",
      autoClose: false,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const checkIfFollowing = async (followerId, followingId, token) => {
    if (!followerId || !followingId || !token || followerId === followingId) {
      return false;
    }
    try {
      const response = await fetch(`${API_URL}/user/following/${followerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        return data.following.some(user => user.userId === followingId);
      }
      return false;
    } catch (err) {
      console.error('Error checking follow status:', err);
      return false;
    }
  };

  const checkIfSaved = async (userId, postId, token) => {
    if (!userId || !token) return false;
    try {
      const response = await fetch(`${API_URL}/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const responseText = await response.text();
        if (!responseText) {
          console.warn('API response is empty.');
          return false;
        }
        try {
          const data = JSON.parse(responseText);
          const savedPosts = data && Array.isArray(data.savedPosts) ? data.savedPosts : [];
          return savedPosts.includes(postId);
        } catch (e) {
          console.error('Failed to parse JSON:', e);
          return false;
        }
      } else {
        console.error('API response not OK:', response.status, await response.text());
        return false;
      }
    } catch (err) {
      console.error('Error checking saved status:', err);
      return false;
    }
  };
  
  useEffect(() => {
    if (post) {
      document.title = `${post.title} - ${postAuthorNickname}`;
    } else {
      document.title = 'QuanPort-Insight Detail'; 
      // document.title = t('postDetail.title'); 
    }
  }, [post, postAuthorNickname, t]);


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const postRes = await fetch(`${API_URL}/posts/${postId}`);
        if (!postRes.ok) throw new Error(`Status ${postRes.status}`);
        const postData = await postRes.json();
        setPost(postData);
        setLikeCount(postData.likes || 0);

        if (postData.authorId) {
          const userRes = await fetch(`${API_URL}/user/nickname/${postData.authorId}`, {
            headers: { 'Authorization': `Bearer ${idToken}` },
          });
          if (userRes.ok) {
            const userData = await userRes.json();
            setPostAuthorNickname(userData.nickname || t('postDetail.unknownUser'));
            setPostAuthorProfileImageUrl(userData.profileImageUrl || DEFAULT_PROFILE_URL);
          } else {
            setPostAuthorNickname(t('postDetail.unknownUser'));
            setPostAuthorProfileImageUrl(DEFAULT_PROFILE_URL);
          }

          if (currentUserId && idToken) {
            if (postData.authorId !== currentUserId) {
              const followingStatus = await checkIfFollowing(currentUserId, postData.authorId, idToken);
              setIsFollowing(followingStatus);
            }
            const savedStatus = await checkIfSaved(currentUserId, postId, idToken);
            setIsSaved(savedStatus);
          }
        } else {
          setPostAuthorNickname(t('postDetail.unknownUser'));
          setPostAuthorProfileImageUrl(DEFAULT_PROFILE_URL);
        }
      } catch (err) {
        console.error('Error fetching post data:', err);
        setError(t('postDetail.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
    setIsLiked(likedPosts.includes(postId));
  }, [postId, idToken, currentUserId, t]); // 3. t 함수를 의존성 배열에 추가합니다.

  const handleLike = async () => {
    if (!idToken) {
      showLoginToast();
      return;
    }
    if (isLiked) return;

    try {
      const response = await fetch(`${API_URL}/posts/${postId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
      });

      if (!response.ok) throw new Error(t('postDetail.likeFailed'));
      const data = await response.json();

      const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
      localStorage.setItem('likedPosts', JSON.stringify([...likedPosts, postId]));

      setIsLiked(true);
      setLikeCount(data.likes);
    } catch (err) {
      console.error('Error handling like:', err);
    }
  };

  const handleSaveToggle = async () => {
    if (!idToken) {
      showLoginToast();
      return;
    }

    const previousIsSaved = isSaved;
    const method = previousIsSaved ? 'DELETE' : 'POST';
    const endpoint = `${API_URL}/posts/${postId}/save`;

    setIsSaved(!previousIsSaved);

    try {
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setIsSaved(previousIsSaved);
        throw new Error(errorData.error || t(previousIsSaved ? 'postDetail.unsaveFailed' : 'postDetail.saveFailed'));
      }
      console.log(`Successfully ${!previousIsSaved ? 'saved' : 'unsaved'} post.`);
    } catch (err) {
      console.error('Error handling save/unsave:', err);
      setIsSaved(previousIsSaved);
    }
  };


  const handleFollowToggle = async () => {
    if (!idToken) {
      showLoginToast();
      return;
    }
    if (!post || !post.authorId || post.authorId === currentUserId) return;

    const targetUserId = post.authorId;
    const method = isFollowing ? 'DELETE' : 'POST';
    const endpoint = `${API_URL}/user/follow`;

    try {
      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ targetUserId: targetUserId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t(isFollowing ? 'postDetail.unfollowFailed' : 'postDetail.followFailed'));
      }

      setIsFollowing(!isFollowing);
    } catch (err) {
      console.error('Error handling follow/unfollow:', err);
    }
  };

  const handleNavigateToAuthorPage = () => {
    if (post && post.authorId) {
      navigate(`/users/${post.authorId}/profile`);
    }
  };

  if (loading) {
    return (
      <div className={CommonStyles.loadingContainer}>
        <div className={CommonStyles.spinner}></div>
        <p className={CommonStyles.message}>{t('postDetail.loading')}</p>
      </div>
    );
  }
  if (error) return <p className={styles.error}>{error}</p>;
  if (!post) return <p className={styles.error}>{t('postDetail.notFound')}</p>;

  const sanitizedContent = DOMPurify.sanitize(post.content);
  const shouldShowFollowButton = post.authorId && post.authorId !== currentUserId;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{post.title}</h1>

      {post.authorId && (
        <div className={styles.authorInfo}>
          <button
            onClick={handleNavigateToAuthorPage}
            className={styles.authorLink}
            title={t('postDetail.navigateToAuthorPageTitle', { nickname: postAuthorNickname })}
          >
            {postAuthorProfileImageUrl && (
              <img
                src={postAuthorProfileImageUrl}
                alt={t('postDetail.profileAlt', { nickname: postAuthorNickname })}
                className={styles.authorProfileImage}
              />
            )}
            <span>{postAuthorNickname}</span>
            <span>↗️</span>
          </button>
          {shouldShowFollowButton && (
            <button
              onClick={handleFollowToggle}
              className={`${CommonStyles.followButton} ${isFollowing ? CommonStyles.following : ''}`}
              title={isFollowing ? t('postDetail.unfollow') : t('postDetail.follow')}
            >
              {isFollowing ? t('postDetail.following') : t('postDetail.follow')}
            </button>
          )}
        </div>
      )}

      <div className={styles.meta}>
        <small className={styles.metaText}>
          {new Date(post.createdAt).toLocaleDateString(i18n.language, { // 4. i18n.language를 사용해 동적으로 날짜 포맷 설정
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </small>
        <div className={styles.likeSection}>
          <button
            onClick={handleSaveToggle}
            className={`${styles.saveButton} ${isSaved ? styles.saved : ''}`}
            title={isSaved ? t('postDetail.removeSavedTitle') : t('postDetail.saveTitle')}
          >
            <span className={styles.saveIcon}>🔖</span>
            <span className={styles.saveText}>{isSaved ? t('postDetail.saved') : t('postDetail.save')}</span>
          </button>
          <button
            onClick={handleLike}
            className={`${styles.likeButton} ${isLiked ? styles.liked : ''}`}
            title={isLiked ? t('postDetail.alreadyLikedTitle') : t('postDetail.like')}
          >
            <span className={styles.likeIcon}>❤️</span>
            <span className={styles.likeText}>{t('postDetail.likeCountText', { count: likeCount })}</span>
          </button>
        </div>
      </div>
       {/* Quill 에디터의 렌더링 구조를 재현합니다. */}
      {/* 이 div는 Quill의 테마 클래스인 ql-snow와 에디터 클래스인 ql-editor를 포함해야 합니다. */}
      <div className={`ql-snow ${styles.content}`}>
        <div 
          className={`${styles.qlEditorOverride} ql-editor`} 
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      </div>

      {/* <div className={`ql-snow ${styles.content}`}>
        <div 
          className="ql-editor" 
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      </div> */}
      {/* <div
        className={styles.content}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      /> */}
      <div className={styles.likeSectionBottom}>
        <button
          onClick={handleLike}
          className={`${styles.likeButton} ${isLiked ? styles.liked : ''}`}
          title={isLiked ? t('postDetail.alreadyLikedTitle') : t('postDetail.like')}
        >
          <span className={styles.likeIcon}>❤️</span>
          <span className={styles.likeText}>{t('postDetail.likeCountText', { count: likeCount })}</span>
        </button>
      </div>
      <CommentSection entityId={postId} API_BASE_URL={API_URL} />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

